import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Ticket as TicketIcon } from 'lucide-react';
import { format } from 'date-fns';
import TicketFeedbackWidget from '@/components/TicketFeedbackWidget';
import SLABadge from '@/components/SLABadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Tickets = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      fetchTickets();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tickets`, {
        withCredentials: true
      });
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/tickets`,
        formData,
        { withCredentials: true }
      );
      
      toast.success('Ticket created successfully');
      setShowCreateDialog(false);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: 'general'
      });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      const detail = error.response?.data?.detail;
      // FastAPI 422 returns detail as an array of validation errors; flatten to a string.
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.[d.loc.length - 1] || 'field'}: ${d.msg}`).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to create ticket');
      toast.error(msg);
    }
  };

  const handleFileUpload = async (ticketId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${BACKEND_URL}/api/tickets/${ticketId}/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success('File uploaded successfully');
      fetchTickets();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'bg-blue-500/20 text-blue-500 border-blue-500/50',
      medium: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
      high: 'bg-orange-500/20 text-orange-500 border-orange-500/50',
      critical: 'bg-destructive/20 text-destructive border-destructive/50'
    };

    return (
      <Badge className={variants[priority] || variants.medium} data-testid={`priority-${priority}`}>
        {priority.toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
      in_progress: 'bg-blue-500/20 text-blue-500 border-blue-500/50',
      resolved: 'bg-primary text-primary-foreground',
      closed: 'bg-muted text-muted-foreground border-muted'
    };

    return (
      <Badge className={variants[status] || variants.open} data-testid={`status-${status}`}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="tickets-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
            <div className="font-black text-xl uppercase tracking-tight">
              NEXT<span className="text-primary">/</span>GEN
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-12">
            <div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-black uppercase mb-2" data-testid="tickets-title">
                SUPPORT TICKETS
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">Create and track your support requests</p>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="font-bold uppercase w-full sm:w-auto" data-testid="create-ticket-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Ticket
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border mx-4 sm:mx-0 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-xl sm:text-2xl font-bold uppercase">Create Support Ticket</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTicket} className="space-y-4">
                  <div>
                    <Label htmlFor="title" className="uppercase text-xs tracking-widest">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="bg-input"
                      data-testid="ticket-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description" className="uppercase text-xs tracking-widest">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      className="bg-input"
                      data-testid="ticket-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="priority" className="uppercase text-xs tracking-widest">Priority</Label>
                      <Select 
                        value={formData.priority} 
                        onValueChange={(value) => setFormData({ ...formData, priority: value })}
                      >
                        <SelectTrigger className="bg-input" data-testid="ticket-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="category" className="uppercase text-xs tracking-widest">Category</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger className="bg-input" data-testid="ticket-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="technical">Technical</SelectItem>
                          <SelectItem value="billing">Billing</SelectItem>
                          <SelectItem value="network">Network</SelectItem>
                          <SelectItem value="security">Security</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" className="w-full font-bold uppercase" data-testid="submit-ticket-btn">
                    Create Ticket
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {tickets.length === 0 ? (
            <Card className="p-12 text-center border-border">
              <TicketIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tickets found</p>
              <p className="text-sm text-muted-foreground mt-2">Create your first support ticket to get started</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket, index) => (
                <motion.div
                  key={ticket.ticket_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-colors" data-testid={`ticket-card-${index}`}>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-bold text-base sm:text-lg">{ticket.title}</h3>
                            {getPriorityBadge(ticket.priority)}
                            {getStatusBadge(ticket.status)}
                            <SLABadge slaState={ticket.sla_state} compact />
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2">{ticket.description}</p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-muted-foreground">
                            <span className="font-mono">{ticket.ticket_id}</span>
                            <span className="hidden sm:inline">Category: {ticket.category}</span>
                            <span>Created: {format(new Date(ticket.created_at), 'MMM dd, yyyy')}</span>
                          </div>
                        </div>
                      </div>
                      
                      {ticket.admin_response && (
                        <div className="mt-4 p-4 bg-muted/50 rounded-sm border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Admin Response</p>
                          <p className="text-sm">{ticket.admin_response}</p>
                        </div>
                      )}

                      {/* Attachments */}
                      {ticket.attachments && ticket.attachments.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Attachments</p>
                          <div className="flex flex-wrap gap-2">
                            {ticket.attachments.map((attachment, idx) => (
                              <a
                                key={idx}
                                href={attachment.data}
                                download={attachment.filename}
                                className="text-xs bg-muted px-3 py-2 rounded-sm border border-border hover:border-primary transition-colors"
                              >
                                {attachment.filename} ({Math.round(attachment.size / 1024)}KB)
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* File Upload */}
                      <div className="mt-4 flex items-center gap-2">
                        <input
                          type="file"
                          id={`file-upload-${ticket.ticket_id}`}
                          className="hidden"
                          onChange={(e) => handleFileUpload(ticket.ticket_id, e)}
                          disabled={uploadingFile}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById(`file-upload-${ticket.ticket_id}`).click()}
                          disabled={uploadingFile}
                        >
                          {uploadingFile ? 'Uploading...' : 'Attach File'}
                        </Button>
                        <span className="text-xs text-muted-foreground">Max 5MB (PDF, Images, Documents)</span>
                      </div>

                      {/* "Was this helpful?" widget — only on closed tickets */}
                      {ticket.status === 'closed' && (
                        <TicketFeedbackWidget ticketId={ticket.ticket_id} compact />
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Tickets;
